import { useStyletron } from "baseui";
import { StyledLink } from "baseui/link";
import { StyledRoot, StyledTable, StyledTableBodyCell, StyledTableBodyRow, StyledTableHeadCell } from "baseui/table-semantic";
import type { Theme, Typography } from "baseui/theme";
import type { ReactHTML, ReactNode } from "react";
import React from "react";
import type { StyleObject } from "styletron-react";
import { parameterize } from "superflected";
import { useHover } from "../../lib/hooks";
import { getChildrenText } from "../../lib/utils";
import { Anchor } from "../edit/shared/Anchor";
import { CodeBlock } from "./CodeBlock";
import { ListItem, OrderedList, UnorderedList } from "./ListItems";
import { TBody, THead } from "./Table";
import { Blockquote, Paragraph, StyledInlineCode } from "./Typography";

export const Heading = ({
  element,
  fontType,
  children,
  $style,
  id,
}: {
  element: keyof ReactHTML;
  fontType: keyof Theme["typography"];
  children: React.ReactNode;
  $style?: StyleObject;
  id?: string;
}) => {
  const [isHovered, hoverProps] = useHover();
  const text = getChildrenText(children);
  const slug = id ?? parameterize(text);
  const [css, $theme] = useStyletron();

  return React.createElement(
    element,
    {
      ...hoverProps,
      id: slug,
      className: css({
        ...$theme.typography[fontType],
        color: $theme.colors.contentPrimary,
        marginTop: $theme.sizing.scale900,
        marginBottom: $theme.sizing.scale700,
        display: "flex",
        alignItems: "center",
        gap: $theme.sizing.scale400,
        ...$style,
      }),
    },
    <React.Fragment>
      {children} <Anchor isVisible={isHovered} slug={slug} element={element} />
    </React.Fragment>
  );
};

const headingComponent = (element: keyof ReactHTML, fontType: keyof Typography) => {
  const HeadingComponent = (props: { children: React.ReactNode; id?: string }) => {
    return <Heading element={element} fontType={fontType} {...props} />;
  };
  return HeadingComponent;
};

export const H1 = headingComponent("h1", "HeadingXLarge");
export const H2 = headingComponent("h2", "HeadingLarge");
export const H3 = headingComponent("h3", "HeadingMedium");
export const H4 = headingComponent("h4", "HeadingSmall");
export const H5 = headingComponent("h5", "HeadingXSmall");
export const H6 = headingComponent("h6", "HeadingXSmall");

export const Link = ({ children, href, $style }: { children: ReactNode; href?: string; $style?: StyleObject }) => {
  const [_css, $theme] = useStyletron();

  const styleOverride: StyleObject = {
    color: $theme.colors.accent500,
    textDecoration: "none",
    lineHeight: "0px",
    marginTop: "0px",
    font: "inherit",
    ...$style,
  };

  const target = href?.startsWith("#") ? "_self" : "_blank";

  return (
    <StyledLink href={href} target={target} style={styleOverride}>
      {children}
    </StyledLink>
  );
};

export { StyledTableBodyCell as TD, StyledTableHeadCell as TH, StyledTableBodyRow as TR } from "baseui/table-semantic";

export const Table = ({ children }: { children: React.ReactNode }) => {
  const [_css, $theme] = useStyletron();
  return (
    <StyledRoot $style={{ marginBottom: $theme.sizing.scale600 }}>
      <StyledTable>{children}</StyledTable>
    </StyledRoot>
  );
};

export const Code = ({ children, inline, ...props }: { children: ReactNode; inline?: boolean; className?: string }) => {
  if (inline) {
    return <StyledInlineCode {...props}>{children}</StyledInlineCode>;
  }
  return <CodeBlock {...props}>{String(children)}</CodeBlock>;
};

export const MarkdownComponents = {
  h1: H1,
  h2: H2,
  h3: H3,
  h4: H4,
  h5: H5,
  h6: H6,
  p: Paragraph,
  li: ListItem as any,
  ol: OrderedList as any,
  ul: UnorderedList as any,
  blockquote: Blockquote,
  code: Code,
  a: Link,
  table: Table,
  thead: THead as any,
  tbody: TBody as any,
  tr: StyledTableBodyRow,
  td: StyledTableBodyCell,
  th: StyledTableHeadCell,
};
